/**
 * The editor for any entity, generated from its registry entry.
 *
 * Sends only the fields that actually changed, because the API's PATCH treats
 * an omitted field as "leave it alone" and an empty one as "clear it" - so
 * sending everything back would rewrite fields nobody touched, and a write in
 * BEST is not free: it re-derives the projection and proves the file still
 * regenerates before it commits.
 */

import { useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FieldControl from './FieldControl'
import { LONG_TEXT_FIELDS, VERIFICATION_SECTIONS } from './verification-model'
import HelpTip from '../common/HelpTip'
import QuantityGroup from './QuantityGroup'
import { quantityStated } from './quantity'
import { ApiError, api } from '../../api'
import { forgetChoices } from '../../hooks/useChoices'
import { useCanWrite, useSchema } from '../../state/AppData'
import { useUnsavedGuard } from '../../state/Unsaved'
import { useToasts } from '../../state/Toasts'
import type { EntityRow, FieldSchema, KindSchema } from '../../schema'
import { editableFields, generatedFields, groupByAttribute, statableFields } from '../../schema'

/** The kind whose enum fields add a value through the part route. */
const PART_KIND = 'design_part'

/** Fields that deserve the full width of the form. */
function isWide(field: FieldSchema): boolean {
  return (
    field.kind === 'doc' ||
    field.kind === 'text_list' ||
    field.kind === 'reference_list' ||
    field.kind === 'text'
  )
}

/**
 * Two values are the same edit if they look the same to the API.
 *
 * A quantity is compared by its NUMBER because the two sides are different
 * shapes: the row carries the composed `'3.5 [SI::kg]'` and the control emits
 * the bare `3.5`, so a plain comparison calls every quantity dirty the moment it
 * is touched - and a save in BEST is a commit, so that is a commit that changes
 * nothing. `quantityStated` answers null for unstated, which compares unequal to
 * 0: clearing a mass is still an edit.
 */
function unchanged(a: unknown, b: unknown, kind?: FieldSchema['kind']): boolean {
  if (kind === 'quantity') return quantityStated(a) === quantityStated(b)
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : []
    const right = Array.isArray(b) ? b : []
    return left.length === right.length && left.every((item, at) => item === right[at])
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b)
  return (a ?? '') === (b ?? '')
}

export interface EntityFormProps {
  kind: KindSchema
  row: EntityRow
  /** Called with the fresh row after a successful save. */
  onSaved?: (row: EntityRow) => void
}

export default function EntityForm({ kind, row, onSaved }: EntityFormProps) {
  const schema = useSchema()
  const { allowed, reason, paused } = useCanWrite()
  const toasts = useToasts()

  const [edits, setEdits] = useState<Record<string, unknown>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string>('')
  const [saving, setSaving] = useState(false)

  const valueOf = (field: FieldSchema) =>
    field.name in edits ? edits[field.name] : row[field.name]

  /**
   * The fields this row may actually state.
   *
   * A part carries an attribute when its type reaches the part def that
   * DECLARES it, and `statable` on the payload is the model's answer for this
   * one part. Without it the editor offered every attribute the profile
   * declares to every part: a structural bracket was asked for its chamber
   * pressure and its inertia tensor, forty-odd controls of which a handful
   * applied.
   *
   * A requirement sends one too, for the same reason: a type's attributes and
   * links belong to the requirement def that declares them, and ungated, a
   * plain L0 was offered an ICD requirement's interface. Every kind the generic
   * detail route serves sends one as well (issue #85), from what its types
   * declare: a test campaign was offered a case's test level, method and
   * verified requirement. A payload with no `statable` - a type BEST cannot
   * follow - is unaffected: `statableFields` passes its fields through.
   */
  const shown = useMemo(
    () =>
      generatedFields(
        editableFields(
          statableFields(kind.fields, (row as Record<string, unknown>).statable),
          row as Record<string, unknown>,
        ),
        // LAST, so it marks whatever survived the two filters above. A
        // requirement typed by an abstract requirement type renders its own
        // statement, so the box for it is read-only rather than an edit the
        // next load discards.
        kind,
        row as Record<string, unknown>,
      ),
    [kind, row],
  )

  /** The same fields, gathered into the structures the model wrote. */
  const groups = useMemo(() => groupByAttribute(shown), [shown])

  const changed = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const field of shown) {
      // A derived field is never sent, even if something managed to edit one.
      // The write path REFUSES it rather than ignoring it, so including one
      // would fail the whole PATCH - taking the edits beside it down with it.
      if (field.derived) continue
      if (field.name in edits && !unchanged(edits[field.name], row[field.name], field.kind)) {
        out[field.name] = edits[field.name]
      }
    }
    return out
  }, [edits, row, shown])

  const dirty = Object.keys(changed).length > 0
  // Leaving this page now would throw the edits away.
  useUnsavedGuard(dirty)

  // Fields stay editable while saving is merely PAUSED (GitLab not responding):
  // the edits are kept, and the person can go on making them.
  const editable = allowed || Boolean(paused)

  const save = async () => {
    setSaving(true)
    setFailure('')
    setFieldErrors({})
    try {
      await api.update(kind.plural, row.id, changed)
      // A suggested field's list is "what is in use", and this may have added to it.
      if (shown.some((field) => field.suggested && field.name in changed)) forgetChoices()
      // Re-read rather than trusting the local values: the write re-derives the
      // projection, and a field can legitimately come back different from what
      // was sent - a bare 'tbr' is stored as 'RequirementStatusKind::tbr' and read back as
      // 'tbr', and a level follows from the type rather than being set.
      const fresh = await api.get(kind.plural, row.id)
      setEdits({})
      toasts.succeeded(`${row.id} saved`)
      onSaved?.(fresh)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      // A field-level complaint is already shown against the field; repeating
      // it in the banner is noise.
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
      toasts.failed(failed.message)
    } finally {
      setSaving(false)
    }
  }

  const renderGroup = (group: (typeof groups)[number]) =>
    // A STRUCTURE IS ONE ROW. A centre of gravity is three fields and one
    // fact, an inertia tensor six about a point that is three more - given
    // a control each they were fifteen rows for four facts, every one
    // labelled with the same three words and a different last one.
    group.isStructured ? (
      <Grid key={group.key} size={{ xs: 12 }}>
        <QuantityGroup
          schema={schema}
          kind={kind}
          group={group}
          valueOf={valueOf}
          onChange={(field, next) =>
            setEdits((current) => ({ ...current, [field.name]: next }))
          }
          disabled={!editable || saving}
          fieldErrors={fieldErrors}
        />
      </Grid>
    ) : (
      renderOne(group.fields[0])
    )

  // A verification element's long text fields sit under Setup / Execution /
  // Results (issues #105, #106), after the short fields. A section none of
  // whose fields apply to this element (a campaign has none) is not drawn.
  const sectioned = kind.name === 'verification'
  const inSection = new Set(VERIFICATION_SECTIONS.flatMap((section) => section.fields))
  const controls = (
    <Grid container spacing={2}>
      {groups
        .filter((group) => !sectioned || !group.fields.some((field) => inSection.has(field.name)))
        .map(renderGroup)}
      {sectioned &&
        VERIFICATION_SECTIONS.map((section) => {
          const here = groups.filter((group) =>
            group.fields.some((field) => section.fields.includes(field.name)))
          if (!here.length) return null
          return (
            <Grid key={section.title} size={{ xs: 12 }}>
              <Typography variant="overline" color="text.secondary" component="h3">
                {section.title}
              </Typography>
              <Grid container spacing={2}>
                {here.map(renderGroup)}
              </Grid>
            </Grid>
          )
        })}
    </Grid>
  )

  function renderOne(field: FieldSchema) {
    return (
        <Grid key={field.name} size={{ xs: 12, md: isWide(field) ? 12 : 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <FieldControl
                schema={schema}
                kind={kind}
                // The row this form is editing, so "+ Add Option" has somewhere
                // to declare a vocabulary. Only enum controls read it.
                //
                // A PART's only. The route it feeds is /api/parts/{id}/choices,
                // so handed a requirement's id it answered 404 - and a
                // requirement's segment and subsystem, which are enums with a
                // base type like a part's fuel, were offering "+ Add option"
                // that could not work. A field that CAN be added to without a
                // row says so itself (`can_add_choice`) and needs no target.
                target={kind.name === PART_KIND ? String(row.id ?? '') : undefined}
                multiline={sectioned && LONG_TEXT_FIELDS.has(field.name)}
                // Say it in the label rather than only greying the control out: a
                // disabled field with no explanation reads as a permission problem,
                // and this one is disabled for everybody. 'on_part' is where the
                // port SITS; changing it would be a different declaration.
                //
                // `help` is blanked and moved to the '?' beside the control. The
                // profile writes a paragraph for every attribute it declares, and
                // forty of them printed under forty boxes is a form nobody can see
                // the bottom of. An ERROR still reaches helperText, which is the
                // one piece of text under a control that must never be hidden.
                field={{
                  ...field,
                  label: field.derived ? `${field.label} · derived` : field.label,
                  help: '',
                }}
                value={valueOf(field)}
                onChange={(next) => setEdits((current) => ({ ...current, [field.name]: next }))}
                // Derived fields are read-only for EVERYONE, permission aside. The
                // write path refuses them, so an editable control here would be a
                // promise the API breaks - and a 200 that changed nothing would be
                // the worst of the three outcomes.
                disabled={!editable || saving || field.derived}
                error={fieldErrors[field.name]}
              />
            </Box>
            {field.help && (
              <Box sx={{ pt: 1 }}>
                <HelpTip title={field.help} label={`What ${field.label} means`} />
              </Box>
            )}
          </Box>
        </Grid>
    )
  }

  return (
    <Stack spacing={2}>
      {!allowed && <Alert severity="info">{reason}</Alert>}
      {failure && <Alert severity="error">{failure}</Alert>}

      {controls}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button
              variant="contained"
              onClick={() => void save()}
              disabled={!allowed || !dirty || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </span>
        </Tooltip>
        <Button onClick={() => setEdits({})} disabled={!dirty || saving}>
          Discard
        </Button>
        {dirty && (
          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
            {Object.keys(changed).length} unsaved change
            {Object.keys(changed).length === 1 ? '' : 's'}
          </Box>
        )}
      </Box>
    </Stack>
  )
}
